/**
 * Numeric mismatch severity tolerances (issue vs verification).
 * Keep in sync with api/utils/numericMismatchTolerance.js
 *
 * Default (adaptive): min(0.01, |codeValue| × 0.5)
 * Overrides below win when set — smaller = stricter (more red/issue).
 *
 * Add a field:  NUMERIC_MISMATCH_FIELD_TOLERANCE['Lights::Watts/Area'] = 0.005
 * Or a section: NUMERIC_MISMATCH_SECTION_TOLERANCE['People'] = 0.001
 */

export const NUMERIC_MISMATCH_MAX_TOLERANCE = 0.01;

/** Per-field absolute tolerance. Key: `${section}::${field}` */
export const NUMERIC_MISMATCH_FIELD_TOLERANCE: Record<string, number> = {
  'People::People/Area': 0.001,
};

/** Per-section absolute tolerance when no field override matches. */
export const NUMERIC_MISMATCH_SECTION_TOLERANCE: Record<string, number> = {
  // Example: People: 0.001,
};

const normalizeFieldName = (field?: string): string =>
  String(field || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\s*\/\s*/g, '/');

export const getToleranceOverride = (
  section?: string,
  field?: string
): number | null => {
  if (!section) return null;
  const normalizedField = normalizeFieldName(field);
  if (normalizedField) {
    const fieldKey = `${section}::${normalizedField}`;
    if (Object.prototype.hasOwnProperty.call(NUMERIC_MISMATCH_FIELD_TOLERANCE, fieldKey)) {
      return NUMERIC_MISMATCH_FIELD_TOLERANCE[fieldKey];
    }
  }
  if (Object.prototype.hasOwnProperty.call(NUMERIC_MISMATCH_SECTION_TOLERANCE, section)) {
    return NUMERIC_MISMATCH_SECTION_TOLERANCE[section];
  }
  return null;
};

export const getNumericMismatchTolerance = (
  numCodeValue: number,
  section?: string,
  field?: string
): number => {
  const override = getToleranceOverride(section, field);
  if (override != null) return override;

  const absCode = Math.abs(numCodeValue);
  if (absCode === 0) return NUMERIC_MISMATCH_MAX_TOLERANCE;
  return Math.min(NUMERIC_MISMATCH_MAX_TOLERANCE, absCode * 0.5);
};

export const getNumericMismatchDifference = (
  numValue: number,
  numCodeValue: number
): number => Math.round(Math.abs(numValue - numCodeValue) * 1e6) / 1e6;
